import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import LegalFooter from "@/components/LegalFooter";
import {
  LogIn, UserPlus, Building2, Phone, Mail, Lock,
  Eye, EyeOff, KeyRound, ArrowLeft, RefreshCw, X, CheckCircle2, Search
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ── Reusable Glass Input ── */
function GlassInput({ icon: Icon, type = "text", className = "", ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <div className="relative">
      {Icon && (
        <Icon
          className={`absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] transition-opacity duration-300 ${focused ? "opacity-80 text-slate-700" : "opacity-40 text-slate-500"}`}
          strokeWidth={1.5}
        />
      )}
      <input
        type={type}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className={`w-full rounded-2xl border border-white/50 bg-white/30 ${Icon ? "pl-12" : "px-5"} pr-5 py-4 text-slate-800 placeholder:text-slate-400 shadow-inner backdrop-blur-xl transition-all duration-300 focus:border-white/80 focus:bg-white/50 focus:outline-none focus:ring-4 focus:ring-white/30 font-[Manrope] text-[15px] ${className}`}
        {...props}
      />
    </div>
  );
}

/* ── Glass Modal Wrapper ── */
function GlassModal({ open, onClose, children }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="absolute inset-0 bg-black/20 backdrop-blur-xl" onClick={onClose} />
          <motion.div
            className="relative z-10 w-full max-w-md mx-4 max-h-[92vh] overflow-y-auto rounded-[2.5rem] border border-white/60 bg-white/45 p-6 sm:p-8 shadow-[0_8px_60px_-12px_rgba(0,0,0,0.1)] backdrop-blur-[60px] overscroll-contain"
            initial={{ opacity: 0, y: 40, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Inner glow edge */}
            <div className="pointer-events-none absolute inset-0 rounded-[2.5rem] border border-white/80 mix-blend-overlay" />
            <button
              onClick={onClose}
              className="absolute top-6 right-6 text-slate-400 hover:text-slate-700 transition-colors"
            >
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ── OTP Input (6 digits) ── */
function OtpInput({ value, onChange }) {
  const refs = useRef([]);
  const digits = value.padEnd(6, " ").split("").slice(0, 6).map((c) => (c === " " ? "" : c));

  const handleKey = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      refs.current[idx - 1]?.focus();
    }
  };

  const handleChange = (idx, val) => {
    const ch = val.replace(/\D/g, "").slice(-1);
    const arr = [...digits];
    arr[idx] = ch;
    onChange(arr.join(""));
    if (ch && idx < 5) refs.current[idx + 1]?.focus();
  };

  return (
    <div className="flex gap-3 justify-center">
      {digits.map((d, i) => (
        <input
          key={`otp-${i}`}
          ref={(el) => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKey(i, e)}
          data-testid={`otp-input-${i + 1}`}
          className="w-12 h-14 rounded-xl border-2 border-slate-200 bg-white text-center text-2xl font-bold text-slate-800 placeholder:text-slate-300 transition-all duration-200 focus:border-teal-400 focus:bg-white focus:outline-none focus:ring-4 focus:ring-teal-100 shadow-sm font-[Manrope]"
        />
      ))}
    </div>
  );
}

/* ── Main Login Component ── */
export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);

  // Registration
  const [showRegister, setShowRegister] = useState(false);
  const [regCompanyName, setRegCompanyName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPhone, setRegPhone] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regConfirmPassword, setRegConfirmPassword] = useState("");
  const [regTermsAccepted, setRegTermsAccepted] = useState(false);
  // ANAF lookup state
  const [regCui, setRegCui] = useState("");
  const [regAddress, setRegAddress] = useState("");
  const [regCuiLookup, setRegCuiLookup] = useState(false);

  // Email verification
  const [showVerify, setShowVerify] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState("");

  // Contact form
  const [showContact, setShowContact] = useState(false);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [contactType, setContactType] = useState("problema_logare");
  const [sendingContact, setSendingContact] = useState(false);
  const [verifyCode, setVerifyCode] = useState("");
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Pornește countdown 60s când se deschide dialogul de verificare
  useEffect(() => {
    if (showVerify) setResendCooldown(60);
  }, [showVerify]);

  // Tick-uri pentru countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  // Forgot password
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotStep, setForgotStep] = useState(1);
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");

  // If already logged in, redirect to dashboard
  useEffect(() => {
    // SEO: setează canonical pentru pagina de login (ca să nu fie marcată ca duplicat al homepage)
    document.title = "Autentificare | e-Tesa";
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", "https://e-tesa.ro/login");

    const stored = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (stored) {
      try {
        const user = JSON.parse(stored);
        if (user?.role) {
          if (user.role === "superadmin") navigate("/superadmin", { replace: true });
          else if (user.role === "master" || user.role === "admin") navigate("/master", { replace: true });
          else if (user.role === "client") navigate("/client", { replace: true });
          else navigate("/worker", { replace: true });
        }
      } catch (error) {
        console.error('Failed to parse stored user for redirect:', error);
      }
    }
    // Deschide automat modalul de înregistrare dacă URL-ul are ?register=1 (din landing page)
    const params = new URLSearchParams(window.location.search);
    if (params.get('register') === '1') {
      setShowRegister(true);
    }
  }, [navigate]);

  /* ── Handlers (same logic, no changes) ── */
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const user = response.data;
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem("user", JSON.stringify(user));
      if (rememberMe) localStorage.setItem("rememberMe", "true");
      else { localStorage.removeItem("rememberMe"); localStorage.removeItem("user"); }

      toast.success(`Bine ai venit, ${user.name}!`);
      if (user.role === "superadmin") navigate("/superadmin", { replace: true });
      else if (user.role === "master" || user.role === "admin") navigate("/master", { replace: true });
      else if (user.role === "client") navigate("/client", { replace: true });
      else navigate("/worker", { replace: true });
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === "email_not_verified") {
        setVerifyEmail(email);
        setShowVerify(true);
        toast.info("Contul nu este verificat. Introdu codul primit pe email.");
      } else {
        toast.error(detail || "Eroare la autentificare");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnafLookup = async () => {
    const cleanCui = (regCui || "").replace(/\D/g, "");
    if (cleanCui.length < 2 || cleanCui.length > 10) {
      toast.error("CUI invalid (2-10 cifre).");
      return;
    }
    setRegCuiLookup(true);
    try {
      const r = await axios.get(`${API}/auth/public-anaf-lookup/${cleanCui}`);
      const d = r.data || {};
      if (d.inactive) toast.warning("Firma este marcată ca INACTIVĂ în ANAF.");
      if (d.name) setRegCompanyName(d.name);
      if (d.address) setRegAddress(d.address);
      toast.success("Date preluate din ANAF.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "CUI negăsit în ANAF.");
    } finally {
      setRegCuiLookup(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!regTermsAccepted) {
      toast.error("Trebuie să accepți Termenii și Politica GDPR pentru a crea contul.");
      return;
    }
    if (regPassword !== regConfirmPassword) { toast.error("Parolele nu coincid"); return; }
    if (regPassword.length < 6) { toast.error("Parola trebuie să aibă minim 6 caractere"); return; }
    setLoading(true);
    try {
      const regResp = await axios.post(`${API}/auth/client-register`, {
        company_name: regCompanyName,
        email: regEmail,
        phone: regPhone,
        password: regPassword,
        cui: (regCui || "").replace(/\D/g, "") || null,
        address: regAddress || null,
        terms_accepted: true,
        terms_accepted_at: new Date().toISOString(),
      });
      setShowRegister(false);
      // Pre-completare email pe formularul de login pentru o experiență fluidă
      setEmail(regEmail);
      const needsVerify = regResp?.data?.requires_verification;
      const newAcctEmail = regEmail;
      setRegCompanyName(""); setRegPhone(""); setRegPassword(""); setRegConfirmPassword(""); setRegEmail("");
      setRegCui(""); setRegAddress(""); setRegTermsAccepted(false);
      if (needsVerify) {
        setVerifyEmail(newAcctEmail);
        setShowVerify(true);
        toast.success("Cont creat! Verifică emailul pentru codul de activare.");
      } else {
        toast.success("Cont creat cu succes! Te poți autentifica acum.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la înregistrare");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-email`, { email: verifyEmail, code: verifyCode });
      const user = response.data.user;
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem("user", JSON.stringify(user));
      setShowVerify(false);
      setVerifyCode("");
      toast.success("Email verificat! Bine ai venit!");
      if (user.role === "superadmin") navigate("/superadmin", { replace: true });
      else if (user.role === "master" || user.role === "admin") navigate("/master", { replace: true });
      else if (user.role === "client") navigate("/client", { replace: true });
      else navigate("/worker", { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Cod invalid");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: verifyEmail });
      toast.success("Cod nou trimis pe email!");
      setResendCooldown(60);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la retrimitere");
    } finally {
      setResending(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotStep(2);
      toast.success("Verifică emailul pentru codul de resetare.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare");
    } finally {
      setLoading(false);
    }
  };

  const handleSendContact = async (e) => {
    e.preventDefault();
    if (!contactMessage.trim()) { toast.error("Descrieți problema"); return; }
    setSendingContact(true);
    try {
      await axios.post(`${API}/auth/contact`, {
        name: contactName,
        email: contactEmail,
        phone: contactPhone,
        message: contactMessage,
        problem_type: contactType,
      });
      toast.success("Mesajul a fost trimis! Vă contactăm în curând.");
      setShowContact(false);
      setContactName(""); setContactEmail(""); setContactPhone(""); setContactMessage("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la trimitere");
    } finally {
      setSendingContact(false);
    }
  };


  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== newPasswordConfirm) { toast.error("Parolele nu coincid"); return; }
    if (newPassword.length < 6) { toast.error("Parola trebuie să aibă minim 6 caractere"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { email: forgotEmail, code: resetCode, new_password: newPassword });
      setShowForgot(false);
      setForgotStep(1); setResetCode(""); setNewPassword(""); setNewPasswordConfirm("");
      setEmail(forgotEmail);
      toast.success("Parola a fost resetată! Te poți autentifica acum.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la resetare");
    } finally {
      setLoading(false);
    }
  };

  /* ── RENDER ── */
  return (
    <div
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ background: "linear-gradient(135deg, #e8f0fe 0%, #f0e6f6 30%, #fef3e2 60%, #e0f5f0 100%)", fontFamily: "Manrope, system-ui, sans-serif" }}
      data-testid="login-page"
    >
      {/* ── Animated background orbs ── */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute w-[700px] h-[700px] rounded-full opacity-50"
          style={{ background: "radial-gradient(circle, #93c5fd 0%, transparent 70%)", top: "-15%", left: "-20%" }}
          animate={{ x: [0, 80, 0], y: [0, 60, 0], scale: [1, 1.15, 1] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute w-[600px] h-[600px] rounded-full opacity-45"
          style={{ background: "radial-gradient(circle, #fdba74 0%, transparent 70%)", bottom: "-15%", right: "-10%" }}
          animate={{ x: [0, -60, 0], y: [0, -80, 0], scale: [1, 1.2, 1] }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute w-[450px] h-[450px] rounded-full opacity-35"
          style={{ background: "radial-gradient(circle, #a78bfa 0%, transparent 70%)", top: "40%", left: "55%" }}
          animate={{ x: [0, -40, 0], y: [0, 50, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute w-[400px] h-[400px] rounded-full opacity-30"
          style={{ background: "radial-gradient(circle, #5eead4 0%, transparent 70%)", top: "10%", right: "20%" }}
          animate={{ x: [0, 50, 0], y: [0, -40, 0], scale: [1, 1.12, 1] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* ── Glass Card ── */}
      <motion.div
        className="relative z-10 w-full max-w-md mx-4 rounded-[2.5rem] border border-white/60 bg-white/40 p-8 sm:p-10 shadow-[0_8px_60px_-12px_rgba(0,0,0,0.08)] backdrop-blur-[60px]"
        initial={{ opacity: 0, y: 40, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        data-testid="login-card"
      >
        {/* Inner glow highlight */}
        <div className="pointer-events-none absolute inset-0 rounded-[2.5rem] border border-white/80 mix-blend-overlay" />

        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img
            src="/etesa-logo.png"
            alt="e-Tesa"
            className="h-24 w-auto drop-shadow-[0_0_30px_rgba(255,255,255,0.15)]"
          />
        </div>

        {/* Login Form */}
        <form onSubmit={handleLogin} className="space-y-5">
          <div className="space-y-2">
            <label className="text-slate-500 text-sm font-medium pl-1">Email</label>
            <GlassInput
              icon={Mail}
              type="email"
              placeholder="nume@companie.ro"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="email-input"
            />
          </div>

          <div className="space-y-2">
            <label className="text-slate-500 text-sm font-medium pl-1">Parolă</label>
            <div className="relative">
              <GlassInput
                icon={Lock}
                type={showPassword ? "text" : "password"}
                placeholder="Introdu parola"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="pr-12"
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeOff className="w-5 h-5" strokeWidth={1.5} /> : <Eye className="w-5 h-5" strokeWidth={1.5} />}
              </button>
            </div>
          </div>

          {/* Remember Me + Forgot Password */}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2.5 cursor-pointer select-none group">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="sr-only peer"
                  data-testid="remember-me-checkbox"
                />
                <div className="w-5 h-5 rounded-md border border-slate-300 bg-white/40 transition-all peer-checked:bg-teal-500 peer-checked:border-teal-500" />
                <CheckCircle2 className="absolute inset-0 w-5 h-5 text-white opacity-0 peer-checked:opacity-100 transition-opacity" strokeWidth={2.5} />
              </div>
              <span className="text-slate-500 text-sm group-hover:text-slate-700 transition-colors">Ține-mă logat</span>
            </label>
            <button
              type="button"
              onClick={() => { setForgotEmail(email); setForgotStep(1); setShowForgot(true); }}
              className="text-sm text-slate-400 hover:text-teal-600 transition-colors"
              data-testid="forgot-password-button"
            >
              Am uitat parola
            </button>
          </div>

          {/* Login Button */}
          <motion.button
            type="submit"
            disabled={loading}
            className="group relative w-full overflow-hidden rounded-2xl bg-gradient-to-r from-teal-500 to-blue-500 px-4 py-4 font-semibold text-white transition-all shadow-[0_4px_20px_rgba(20,184,166,0.3)] disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="login-button"
          >
            <span className="relative z-10 flex items-center justify-center gap-2 text-[15px]">
              <LogIn className="w-4 h-4" strokeWidth={2} />
              {loading ? "Se încarcă..." : "Intră în cont"}
            </span>
          </motion.button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-4 my-6">
          <div className="flex-1 h-px bg-slate-200/60" />
          <span className="text-slate-300 text-xs font-medium uppercase tracking-widest">sau</span>
          <div className="flex-1 h-px bg-slate-200/60" />
        </div>

        {/* Register Button */}
        <motion.button
          type="button"
          onClick={() => setShowRegister(true)}
          className="w-full rounded-2xl border border-white/50 bg-white/20 px-4 py-4 font-semibold text-slate-700 transition-all hover:bg-white/40 backdrop-blur-md text-[15px]"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          data-testid="register-button"
        >
          <span className="flex items-center justify-center gap-2">
            <UserPlus className="w-4 h-4 opacity-50 text-slate-500" strokeWidth={1.5} />
            Creare cont nou (Angajator)
          </span>
        </motion.button>
      </motion.div>

      {/* ═════════════════════════════════════════════
          REGISTRATION DIALOG
      ═════════════════════════════════════════════ */}
      <GlassModal open={showRegister} onClose={() => setShowRegister(false)}>
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-slate-800 tracking-tight flex items-center gap-2" style={{ fontFamily: "Space Grotesk, system-ui" }}>
              <Building2 className="w-6 h-6 opacity-50 text-slate-500" strokeWidth={1.5} />
              Creare Cont Angajator
            </h2>
            <p className="text-slate-400 text-sm mt-1">Completați datele pentru a crea un cont de administrator</p>
          </div>
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-slate-500 text-sm font-medium pl-1">CUI firmă (opțional — preia date din ANAF)</label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <GlassInput
                    icon={Search}
                    inputMode="numeric"
                    placeholder="ex: 44303561"
                    value={regCui}
                    onChange={(e) => setRegCui(e.target.value.replace(/\D/g, ""))}
                    maxLength={10}
                    data-testid="register-cui-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAnafLookup}
                  disabled={regCuiLookup || !regCui}
                  className="px-4 rounded-2xl bg-gradient-to-r from-teal-500 to-blue-500 text-white font-medium shadow-md disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                  data-testid="register-anaf-lookup-btn"
                  title="Caută date în ANAF"
                >
                  {regCuiLookup ? (
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">Caută</span>
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-slate-500 text-sm font-medium pl-1">Nume Companie / Administrator</label>
              <GlassInput icon={Building2} placeholder="SC Compania Mea SRL" value={regCompanyName} onChange={(e) => setRegCompanyName(e.target.value)} required />
            </div>
            {regAddress && (
              <div className="space-y-1.5">
                <label className="text-slate-500 text-sm font-medium pl-1">Adresă (din ANAF)</label>
                <GlassInput icon={Building2} value={regAddress} onChange={(e) => setRegAddress(e.target.value)} placeholder="Adresă sediu social" />
              </div>
            )}
            <div className="space-y-1.5">
              <label className="text-slate-500 text-sm font-medium pl-1">Email</label>
              <GlassInput icon={Mail} type="email" placeholder="email@companie.ro" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} required />
              <p className="text-xs text-slate-500 pl-1 leading-relaxed flex items-start gap-1.5">
                <Mail className="w-3 h-3 mt-0.5 flex-shrink-0 text-teal-500" />
                <span>
                  Recomandăm utilizarea unui email la care aveți acces — prin acesta vom comunica cu dumneavoastră
                  și vă va fi util la recuperarea contului în caz că uitați parola.
                </span>
              </p>
            </div>
            <div className="space-y-1.5">
              <label className="text-slate-500 text-sm font-medium pl-1">Telefon</label>
              <GlassInput icon={Phone} type="tel" placeholder="07XX XXX XXX" value={regPhone} onChange={(e) => setRegPhone(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <label className="text-slate-500 text-sm font-medium pl-1">Parolă</label>
              <GlassInput icon={Lock} type="password" placeholder="Minim 6 caractere" value={regPassword} onChange={(e) => setRegPassword(e.target.value)} required minLength={6} />
            </div>
            <div className="space-y-1.5">
              <label className="text-slate-500 text-sm font-medium pl-1">Confirmă Parola</label>
              <GlassInput icon={Lock} type="password" placeholder="Repetă parola" value={regConfirmPassword} onChange={(e) => setRegConfirmPassword(e.target.value)} required />
            </div>
            <div className="flex items-start gap-2.5 px-1 pt-1">
              <input
                id="reg-terms-checkbox"
                type="checkbox"
                checked={regTermsAccepted}
                onChange={(e) => setRegTermsAccepted(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
                data-testid="register-terms-checkbox"
              />
              <label htmlFor="reg-terms-checkbox" className="text-xs text-slate-600 leading-relaxed cursor-pointer">
                Am citit și sunt de acord cu{" "}
                <a href="/legal?tab=terms" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline font-medium">Termenii și Condițiile</a>{" "}
                și{" "}
                <a href="/legal?tab=privacy" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline font-medium">Politica de Confidențialitate (GDPR)</a>{" "}
                ale platformei e-Tesa. <span className="text-red-500">*</span>
              </label>
            </div>
            <motion.button
              type="submit"
              disabled={loading || !regTermsAccepted}
              className="w-full rounded-2xl bg-gradient-to-r from-teal-500 to-blue-500 px-4 py-4 font-semibold text-white shadow-[0_4px_20px_rgba(20,184,166,0.3)] disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: regTermsAccepted ? 1.02 : 1 }}
              whileTap={{ scale: regTermsAccepted ? 0.98 : 1 }}
              data-testid="register-submit-button"
            >
              {loading ? "Se procesează..." : "Creează Cont"}
            </motion.button>
          </form>
        </div>
      </GlassModal>

      {/* ═════════════════════════════════════════════
          EMAIL VERIFICATION DIALOG
      ═════════════════════════════════════════════ */}
      <GlassModal open={showVerify} onClose={() => setShowVerify(false)}>
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-teal-50 border border-teal-100 flex items-center justify-center">
              <Mail className="w-7 h-7 text-teal-500" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 tracking-tight" style={{ fontFamily: "Space Grotesk, system-ui" }}>
              Verificare Email
            </h2>
            <p className="text-slate-400 text-sm mt-1">
              Am trimis un cod de 6 cifre pe <span className="text-slate-600 font-medium">{verifyEmail}</span>
            </p>
          </div>
          <form onSubmit={handleVerifyEmail} className="space-y-5">
            <OtpInput value={verifyCode} onChange={setVerifyCode} />
            <motion.button
              type="submit"
              disabled={loading || verifyCode.length !== 6}
              className="w-full rounded-2xl bg-gradient-to-r from-teal-500 to-blue-500 px-4 py-4 font-semibold text-white shadow-[0_4px_20px_rgba(20,184,166,0.3)] disabled:opacity-50"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="verify-code-button"
            >
              {loading ? "Se verifică..." : "Verifică"}
            </motion.button>
            <div className="text-center">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resending || resendCooldown > 0}
                className="text-sm text-slate-400 hover:text-teal-600 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:text-slate-400"
                data-testid="resend-code-button"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${resending ? "animate-spin" : ""}`} strokeWidth={1.5} />
                {resending
                  ? "Se trimite..."
                  : resendCooldown > 0
                  ? `Trimite cod nou (${resendCooldown}s)`
                  : "Trimite cod nou"}
              </button>
            </div>
          </form>
        </div>
      </GlassModal>

      {/* ═════════════════════════════════════════════
          FORGOT PASSWORD DIALOG
      ═════════════════════════════════════════════ */}
      <GlassModal
        open={showForgot}
        onClose={() => { setShowForgot(false); setForgotStep(1); setResetCode(""); setNewPassword(""); setNewPasswordConfirm(""); }}
      >
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-50 border border-amber-100 flex items-center justify-center">
              <KeyRound className="w-7 h-7 text-amber-500" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 tracking-tight" style={{ fontFamily: "Space Grotesk, system-ui" }}>
              Recuperare Parolă
            </h2>
            <p className="text-slate-400 text-sm mt-1">
              {forgotStep === 1
                ? "Introdu adresa de email pentru codul de resetare"
                : <>Am trimis un cod pe <span className="text-slate-600 font-medium">{forgotEmail}</span></>}
            </p>
          </div>

          {forgotStep === 1 ? (
            <form onSubmit={handleForgotPassword} className="space-y-5">
              <div className="space-y-1.5">
                <label className="text-slate-500 text-sm font-medium pl-1">Email</label>
                <GlassInput
                  icon={Mail}
                  type="email"
                  placeholder="email@companie.ro"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              <motion.button
                type="submit"
                disabled={loading}
                className="w-full rounded-2xl bg-gradient-to-r from-teal-500 to-blue-500 px-4 py-4 font-semibold text-white shadow-[0_4px_20px_rgba(20,184,166,0.3)] disabled:opacity-50"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="forgot-send-button"
              >
                {loading ? "Se trimite..." : "Trimite codul"}
              </motion.button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-slate-500 text-sm font-medium pl-1">Cod de resetare</label>
                <OtpInput value={resetCode} onChange={setResetCode} />
              </div>
              <div className="space-y-1.5">
                <label className="text-slate-500 text-sm font-medium pl-1">Parolă nouă</label>
                <GlassInput icon={Lock} type="password" placeholder="Minim 6 caractere" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} data-testid="new-password-input" />
              </div>
              <div className="space-y-1.5">
                <label className="text-white/50 text-sm font-medium pl-1">Confirmă parola nouă</label>
                <GlassInput icon={Lock} type="password" placeholder="Repetă parola" value={newPasswordConfirm} onChange={(e) => setNewPasswordConfirm(e.target.value)} required data-testid="new-password-confirm-input" />
              </div>
              <motion.button
                type="submit"
                disabled={loading || resetCode.length !== 6}
                className="w-full rounded-2xl bg-gradient-to-r from-teal-500 to-blue-500 px-4 py-4 font-semibold text-white shadow-[0_4px_20px_rgba(20,184,166,0.3)] disabled:opacity-50"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="reset-password-button"
              >
                {loading ? "Se procesează..." : "Resetează parola"}
              </motion.button>
              <button
                type="button"
                onClick={() => setForgotStep(1)}
                className="w-full text-sm text-slate-400 hover:text-teal-600 flex items-center justify-center gap-1 transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                Înapoi
              </button>
            </form>
          )}
        </div>
      </GlassModal>
      {!showRegister && (
        <div className="fixed bottom-3 left-0 right-0 text-center z-40 pointer-events-none">
          <div className="flex justify-center px-3 pointer-events-none">
            <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-slate-200 px-3 py-2 max-w-xl w-full shadow-sm pointer-events-auto" data-testid="login-footer-card">
              <LegalFooter compact onContactClick={() => setShowContact(true)} />
            </div>
          </div>
        </div>
      )}

      {/* Contact Form Modal */}
      {showContact && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-teal-600" />
                  Semnalează o Problemă
                </h3>
                <button onClick={() => setShowContact(false)} className="text-slate-400 hover:text-slate-600 text-xl">&times;</button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Mesajul va fi trimis la contact@e-tesa.ro</p>
            </div>
            <form onSubmit={handleSendContact} className="p-5 space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Tip problemă</label>
                <select
                  value={contactType}
                  onChange={(e) => setContactType(e.target.value)}
                  className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-400 focus:ring-1 focus:ring-teal-400"
                  data-testid="contact-type-select"
                >
                  <option value="problema_logare">Nu mă pot autentifica</option>
                  <option value="problema_cont">Problemă cu contul</option>
                  <option value="eroare_aplicatie">Eroare în aplicație</option>
                  <option value="sugestie">Sugestie / Îmbunătățire</option>
                  <option value="altele">Altele</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Nume</label>
                <input type="text" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Numele dvs." className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="contact-name" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-slate-600">Email</label>
                  <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="email@exemplu.ro" className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="contact-email" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Telefon</label>
                  <input type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="07XX XXX XXX" className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="contact-phone" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Descrierea problemei *</label>
                <textarea value={contactMessage} onChange={(e) => setContactMessage(e.target.value)} placeholder="Descrieți problema întâmpinată..." rows={4} className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm resize-none" required data-testid="contact-message" />
              </div>
              <button type="submit" disabled={sendingContact || !contactMessage.trim()} className="w-full rounded-xl bg-teal-600 hover:bg-teal-700 text-white py-3 text-sm font-semibold disabled:opacity-50 transition-colors" data-testid="contact-submit-btn">
                {sendingContact ? "Se trimite..." : "Trimite Mesajul"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

